import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

const EnglishSummaryPage: React.FC = () => {
  // FAQ設定
  const faqs = [
    {
      question: '英語が苦手な子は、どこから始めればいいですか？',
      answer: 'まずは「be動詞」と「一般動詞」の違いを理解することから始めましょう。この2つの使い分けができないと、文法全体でつまずきます。無料プリントで基本文を繰り返し書いて、体で覚えることが大切です。',
    },
    {
      question: '文法の順番は、どう整理すればいいですか？',
      answer: 'be動詞→一般動詞→時制（現在・過去・未来）→比較→不定詞/動名詞→関係代名詞の順で進めると、段階的に理解できます。前の単元が理解できていないと次に進めないため、焦らず一つずつ固めましょう。',
    },
    {
      question: '英語の長文読解が苦手な子への声かけは？',
      answer: '「全部読もうとしなくていいよ」と伝えることが大切です。まずは「誰が」「何をした」を見つける練習から始めましょう。段落ごとに要点を確認しながら読むと、長文への抵抗感が減ります。',
    },
    {
      question: '中学英語の無料教材サイトは、どれを選べばいいですか？',
      answer: '文法の基礎固めなら「ちびむすドリル」、長文読解なら「Study-X」がおすすめです。リスニング対策には「平均ドリル」や「eboard」の動画教材が便利です。',
    },
    {
      question: '居場所や家庭で英語を教える際の注意点は？',
      answer: '「完璧な発音」や「難しい文法用語」にこだわりすぎないことが大切です。まずは「英語で伝える楽しさ」を感じてもらい、間違いを恐れずに声に出す習慣をつけましょう。',
    },
  ];

  // SEO設定
  useSEO({
    title: '中学英語まとめ｜文法・読解・リスニング学習ガイド｜キョウコレ',
    description: '中学英語の学習内容を学年別・単元別に整理。文法の整理順と、読解・リスニング対策、おすすめの無料教材サイトをご紹介します。',
    keywords: '中学英語,英語まとめ,文法,読解,リスニング,学習ガイド,無料教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/english-summary',
    jsonLd: generateWebPageSchema(
      '中学英語まとめ｜文法・読解・リスニング学習ガイド｜キョウコレ',
      '中学英語の学習内容を学年別・単元別に整理。文法の整理順と、読解・リスニング対策、おすすめの無料教材サイトをご紹介します。',
      '/features/english-summary'
    ),
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-block w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-4xl mb-4">
              🌍
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学英語の無料教材まとめ
            </h1>
            <p className="text-lg text-gray-600">
              学年別・文法別に整理した学習ガイドと、おすすめの無料教材サイトをご紹介
            </p>
          </div>

          {/* このページでわかること */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-4 border-green-500 pb-2">
              このページでわかること
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>中学英語の文法を「be動詞→一般動詞→時制→比較→不定詞/動名詞」の順で整理する方法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>学年別・文法別のつまずきポイントと攻略法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>無料で使える英語教材サイトの特徴と使い分け</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>居場所や家庭で英語を教える際のコツ</span>
              </li>
            </ul>
          </div>

          {/* 英語はこう整理すると進めやすい */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              英語はこう整理すると進めやすい
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                中学英語は「文法」「読解」「リスニング」「ライティング」の4技能で構成されていますが、その土台となるのが「文法の整理」です。文法がしっかり理解できていれば、読解もリスニングもスムーズに進められます。
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>文法の整理順：</strong>英語が苦手な子の多くは、「be動詞と一般動詞の違い」でつまずいています。この2つの使い分けができないと、時制や疑問文・否定文でも混乱します。まずはbe動詞と一般動詞の基礎を固めることが、英語全体の理解につながります。
              </p>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">学習の進め方（推奨順序）</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                    <div>
                      <strong>be動詞と一般動詞の区別</strong>：この2つの使い分けを徹底的に練習
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                    <div>
                      <strong>時制の理解</strong>：現在形→過去形→未来形の順で進める
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                    <div>
                      <strong>比較・不定詞・動名詞</strong>：応用文法を段階的に習得
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                    <div>
                      <strong>長文読解・リスニング</strong>：文法知識を活かして実践力を養う
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* 学年別・文法別の整理 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              学年別・文法別の整理
            </h2>

            {/* 中1英語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">1</span>
                中1英語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中1では「be動詞」「一般動詞」「時制」という英語の基礎を学びます。ここでつまずくと、中2・中3で苦労するため、丁寧に進めることが大切です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">be動詞（am/is/are）</h4>
                  <p className="text-sm text-gray-600">
                    「〜です」「〜にいます」を表す動詞。疑問文・否定文の作り方を習得。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">一般動詞</h4>
                  <p className="text-sm text-gray-600">
                    「〜する」を表す動詞。三人称単数のsに注意。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">現在進行形</h4>
                  <p className="text-sm text-gray-600">
                    「〜している」を表す形。be動詞+〜ingの形を理解。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">過去形</h4>
                  <p className="text-sm text-gray-600">
                    規則動詞と不規則動詞の過去形を覚える。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">助動詞（can）</h4>
                  <p className="text-sm text-gray-600">
                    「〜できる」を表す助動詞の使い方。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中2英語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">2</span>
                中2英語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中2では「未来形」「比較」「不定詞」「動名詞」など、応用文法が増えます。中1の基礎がしっかりしていれば、スムーズに進められます。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">未来形（will/be going to）</h4>
                  <p className="text-sm text-gray-600">
                    「〜するつもり」「〜するだろう」を表す形。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">助動詞（must/have to/should）</h4>
                  <p className="text-sm text-gray-600">
                    「〜しなければならない」「〜すべき」を表す助動詞。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">不定詞（to+動詞の原形）</h4>
                  <p className="text-sm text-gray-600">
                    「〜すること」「〜するために」を表す形。3つの用法を理解。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">動名詞（〜ing）</h4>
                  <p className="text-sm text-gray-600">
                    「〜すること」を表す形。不定詞との使い分けに注意。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">比較（比較級・最上級）</h4>
                  <p className="text-sm text-gray-600">
                    「〜より〜」「最も〜」を表す形。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中3英語 */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">3</span>
                中3英語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中3では「現在完了」「受動態」「関係代名詞」など、高校英語につながる重要文法を学びます。高校受験対策も本格化するため、計画的な学習が必要です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">現在完了（have/has+過去分詞）</h4>
                  <p className="text-sm text-gray-600">
                    「〜したことがある」「〜してしまった」を表す形。3つの用法を理解。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">受動態（be+過去分詞）</h4>
                  <p className="text-sm text-gray-600">
                    「〜される」を表す形。能動態との書き換えを習得。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">関係代名詞（who/which/that）</h4>
                  <p className="text-sm text-gray-600">
                    2つの文をつなぐ代名詞。長文読解でも頻出。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">分詞（現在分詞・過去分詞）</h4>
                  <p className="text-sm text-gray-600">
                    名詞を修飾する形。「〜している」「〜された」を表す。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">間接疑問文</h4>
                  <p className="text-sm text-gray-600">
                    「〜かどうか」「何が〜か」を表す疑問文。<Link to="/materials?subject=英語" className="text-green-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* おすすめ無料教材サイト */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              おすすめ無料教材サイト一覧
            </h2>
            <p className="text-gray-700 mb-6">
              中学英語の学習に役立つ無料教材サイトを厳選しました。それぞれの特徴を理解して、目的に合わせて使い分けましょう。
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://www.study-x.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
                  >
                    Study-X 無料学習プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>長文読解問題50問以上。読解力強化に最適
                </p>
                <p className="text-sm text-gray-700">
                  英語長文問題が豊富で、繰り返し解くことで読解力が確実に向上します。高校受験対策にも効果的です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://min-pri.com/english-pri/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
                  >
                    みんぷり 英語プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>高校受験対策の文法・長文読解に特化
                </p>
                <p className="text-sm text-gray-700">
                  高校受験に特化したプリントが豊富で、自由に使える点が保護者・先生に好評です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://happylilac.net/highschool.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
                  >
                    ちびむすドリル【中学生】
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>基礎文法から応用まで幅広くカバー。印刷向け
                </p>
                <p className="text-sm text-gray-700">
                  学年別・単元別に整理されたプリントが豊富です。基礎固めに特化しており、英語が苦手な子でも取り組みやすい内容です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://heikin.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
                  >
                    平均ドリル
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>無料授業動画とプリントがセット。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  授業動画を見てから問題を解く形式で、自宅学習に最適です。リスニング対策にも活用できます。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <Link
                    to="/sites"
                    className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
                  >
                    その他の英語教材サイトを見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </h3>
                <p className="text-sm text-gray-700">
                  キョウコレでは、さらに多くの無料教材サイトを紹介しています。教科や単元で絞り込んで検索できます。
                </p>
              </div>
            </div>
          </section>

          {/* 居場所・家庭での使い方 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              居場所・家庭での使い方
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                英語は「完璧な発音」や「難しい文法用語」にこだわりすぎず、「伝える楽しさ」を感じてもらうことが大切です。居場所や家庭で英語を教える際のコツをまとめました。
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. 「声に出して読む」習慣をつける</h3>
                  <p className="text-gray-700 leading-relaxed">
                    英語は声に出すことで、リズムや発音が体に染み込みます。「この文、声に出して読んでみようか」と声をかけて、一緒に音読する時間を作りましょう。間違いを恐れずに声に出す習慣が、英語力の土台になります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. 「日本語に訳さず、イメージで理解する」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    長文読解では、すべてを日本語に訳そうとすると時間がかかります。「誰が」「何をした」を見つけて、イメージで理解する練習をしましょう。「この文、誰が何をしたか分かる？」と問いかけると、子どもも考えやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. 「間違いを責めない」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    英語が苦手な子は、「間違えたら恥ずかしい」と感じがちです。間違いを責めず、「ここまで分かったね」「次はこうしてみようか」と前向きな声かけを心がけましょう。安心して間違えられる環境が、英語力を伸ばします。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. 活用レシピを参考にする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    キョウコレの<Link to="/recipes" className="text-green-600 hover:underline">活用レシピ</Link>では、他の居場所や家庭での実践例が共有されています。「こんな声かけが効果的だった」「この教材の使い方が良かった」といったアイデアを参考にしてみましょう。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-sky-500 pb-2">
              よくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ボラタス導線 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-sky-500 pb-2">
              オンライン学習支援を探すなら（ボラタス）
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                教材だけでなく、オンラインで学習をサポートしてくれるボランティアを探すこともできます。居場所や家庭での学習支援に、ぜひご活用ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://boratasu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスでボランティアを探す
                  <i className="ri-external-link-line ml-2"></i>
                </a>
                <a
                  href="https://zion-craft.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスについて詳しく知る
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-sky-500 to-blue-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学英語は、文法の整理から始めましょう
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              このページで紹介した無料教材サイトや学習のコツを活用して、<br />
              子どもたちの「分かった！」を増やしていきましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/materials?subject=英語"
                className="inline-block px-6 py-3 bg-white text-sky-600 font-medium rounded-lg hover:bg-sky-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                英語の単元ガイド一覧を見る
              </Link>
              <Link
                to="/sites"
                className="inline-block px-6 py-3 bg-white text-sky-600 font-medium rounded-lg hover:bg-sky-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                教材サイト一覧を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EnglishSummaryPage;
